import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useLines } from '../../hooks/useLines';
import { useMasters } from '../../hooks/useMasters';
import { fine, money, today } from '../../utils/format';
import { ErrorNotice, PageHeader } from '../../components/ui';
import { RatePanel } from '../../components/RatePanel';
import { DateInput, Field, Select } from '../../components/ui/Form';
import { CellInput, CellSelect, LineRow, LineTable } from '../../components/ui/LineTable';
import type { QuoteLine, ScannedItem } from '../../types';

interface OldLine {
  description: string;
  metalId: string;
  testedPurityId: string;
  grossWt: string;
  stoneWt: string;
  meltingLossPct: string;
}

interface NewLine {
  item: ScannedItem;
  quote: QuoteLine;
}

const blankOld = (): OldLine => ({
  description: '',
  metalId: '',
  testedPurityId: '',
  grossWt: '',
  stoneWt: '',
  meltingLossPct: '',
});

interface Posted {
  exchangeId: string;
  docNo: string;
  purchaseDocNo: string;
  saleDocNo: string;
  oldValue: string;
  newValue: string;
  /** Negative when the old gold was worth more than the new jewellery. */
  netReceivable: string;
}

/**
 * Customer brings old gold and buys new jewellery.
 *
 * This is deliberately NOT a discount on the sale. The customer sold metal and
 * bought jewellery: two documents, two sets of accounting entries, and tax on
 * the full value of the new piece. Treating it as a discount understates
 * revenue, understates the tax due, and loses the metal that came in.
 */
export function ExchangePage() {
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const masters = useMasters();
  const { branches, defaultBranchId } = useBranches();
  const oldLines = useLines<OldLine>(blankOld);

  const [customerId, setCustomerId] = useState('');
  const [docDate, setDocDate] = useState(today());
  const [branchId, setBranchId] = useState('');
  const [testingMethod, setTestingMethod] = useState('TOUCHSTONE');
  const [barcode, setBarcode] = useState('');
  const [newLines, setNewLines] = useState<NewLine[]>([]);
  const [cash, setCash] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [posted, setPosted] = useState<Posted | null>(null);

  const customers = useQuery({ queryKey: ['customers', ''], queryFn: () => endpoints.parties.customers() });
  const customerList = (customers.data?.data ?? []) as unknown as { id: string; name: string }[];

  const scan = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!barcode.trim()) return;
    setError(null);
    try {
      const item = await endpoints.inventory.scan(barcode.trim(), branchId || defaultBranchId || undefined);
      const quote = await endpoints.pricing.quote({ stockItemId: item['id'] });
      setNewLines((current) => [
        ...current,
        { item: item as unknown as ScannedItem, quote: quote as unknown as QuoteLine },
      ]);
      setBarcode('');
    } catch (err) {
      setError(err);
    }
  };

  const saleSum = newLines.reduce((a, l) => a + Number(l.quote.lineTotal), 0);
  const saleTotal = Math.round(saleSum);

  const create = useMutation({
    mutationFn: () =>
      api.post<Posted>(
        '/exchanges',
        {
          branchId: branchId || defaultBranchId,
          docDate,
          customerId,
          oldGold: {
            testingMethod,
            lines: oldLines.values.map((l) => ({
              ...(l.description ? { description: l.description } : {}),
              metalId: l.metalId,
              testedPurityId: l.testedPurityId,
              grossWt: l.grossWt,
              ...(l.stoneWt ? { stoneWt: l.stoneWt } : {}),
              ...(l.meltingLossPct ? { meltingLossPct: l.meltingLossPct } : {}),
            })),
          },
          sale: {
            lines: newLines.map((l) => ({ stockItemId: l.item.id })),
            payments: cash && Number(cash) > 0 ? [{ mode: 'CASH', amount: cash }] : [],
          },
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (result) => {
      setPosted(result);
      oldLines.reset();
      setNewLines([]);
      setCash('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const ready =
    customerId !== '' &&
    newLines.length > 0 &&
    oldLines.values.every((l) => l.metalId && l.testedPurityId && l.grossWt);

  return (
    <>
      <PageHeader
        title="Exchange"
        subtitle="Old gold in, new jewellery out. Two documents, not a discount — the tax follows the full value of what was sold."
      />
      <RatePanel />
      <ErrorNotice error={error} />

      <p className="mb-4 rounded border border-ink/15 bg-white p-3 text-xs text-ink-soft">
        If the old gold turns out to be worth more than the jewellery being bought, this will be refused — the shop owes
        the customer money, which is an old gold receipt and a refund, not an exchange. Record it on the Old gold screen
        instead.
      </p>

      {posted ? (
        <div className="mb-4 rounded border border-success/30 bg-success/5 p-4 text-sm">
          <p>
            Old gold receipt <span className="num font-medium">{posted.purchaseDocNo}</span> for{' '}
            <span className="num">{money(posted.oldValue)}</span>, invoice{' '}
            <span className="num font-medium">{posted.saleDocNo}</span> for{' '}
            <span className="num">{money(posted.newValue)}</span>.
          </p>
          <p className="mt-1 font-medium">
            Still to collect: <span className="num">{money(posted.netReceivable)}</span>
          </p>
          <p className="mt-1 text-xs text-ink-soft">
            Two documents, both readable on their own. The tax was charged on the full {money(posted.newValue)}, not on
            the balance.
          </p>
        </div>
      ) : null}

      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-6">
        <div className="card grid gap-3 md:grid-cols-4">
          <Field label="Customer" required>
            <Select
              value={customerId}
              onChange={setCustomerId}
              options={customerList.map((c) => ({ value: c.id, label: c.name }))}
              placeholder={customerList.length === 0 ? 'Add a customer first' : 'Choose…'}
            />
          </Field>
          <Field label="Date" required>
            <DateInput value={docDate} onChange={(v) => setDocDate(v)} />
          </Field>
          <Field label="Old gold tested by" required>
            <Select
              value={testingMethod}
              onChange={setTestingMethod}
              options={[
                { value: 'TOUCHSTONE', label: 'Touchstone' },
                { value: 'XRF', label: 'XRF machine' },
                { value: 'CARAT_METER', label: 'Carat meter' },
                { value: 'FIRE_ASSAY', label: 'Fire assay' },
              ]}
            />
          </Field>
          {branches.length > 1 ? (
            <Field label="Branch" required>
              <Select
                value={branchId || (defaultBranchId ?? '')}
                onChange={setBranchId}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
          ) : null}
        </div>

        <section>
          <h2 className="mb-2 text-sm font-semibold">Old gold coming in</h2>
          <LineTable
            head={['Description', 'Metal', 'Tested purity', 'Gross wt', 'Stone wt', 'Melting loss %']}
            addLabel="Add item"
            onAdd={oldLines.add}
          >
            {oldLines.lines.map((line, index) => (
              <LineRow
                key={line.key}
                index={index}
                removable={oldLines.lines.length > 1}
                onRemove={() => oldLines.remove(line.key)}
              >
                <td className="td">
                  <CellInput
                    width="w-44"
                    value={line.description}
                    onChange={(v) => oldLines.update(line.key, { description: v })}
                    placeholder="Old chain"
                  />
                </td>
                <td className="td">
                  <CellSelect
                    width="w-24"
                    value={line.metalId}
                    onChange={(v) => oldLines.update(line.key, { metalId: v, testedPurityId: '' })}
                    options={masters.activeMetals.map((m) => ({ value: m.id, label: m.code }))}
                  />
                </td>
                <td className="td">
                  <CellSelect
                    width="w-24"
                    value={line.testedPurityId}
                    onChange={(v) => oldLines.update(line.key, { testedPurityId: v })}
                    options={masters.puritiesFor(line.metalId).map((p) => ({ value: p.id, label: p.code }))}
                  />
                </td>
                <td className="td">
                  <CellInput
                    numeric
                    width="w-24"
                    value={line.grossWt}
                    onChange={(v) => oldLines.update(line.key, { grossWt: v })}
                    placeholder="20.000"
                  />
                </td>
                <td className="td">
                  <CellInput
                    numeric
                    width="w-24"
                    value={line.stoneWt}
                    onChange={(v) => oldLines.update(line.key, { stoneWt: v })}
                  />
                </td>
                <td className="td">
                  <CellInput
                    numeric
                    width="w-20"
                    value={line.meltingLossPct}
                    onChange={(v) => oldLines.update(line.key, { meltingLossPct: v })}
                    placeholder="2.00"
                  />
                </td>
              </LineRow>
            ))}
          </LineTable>
          <p className="mt-2 text-xs text-ink-soft">
            What the old gold is worth is calculated by the server from the tested purity and the approved purchase
            rate. It is never typed in.
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold">New jewellery going out</h2>

          <div className="mb-3 flex gap-2">
            <input
              className="field max-w-xs"
              placeholder="Scan the new piece"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
            <button type="button" className="btn-ghost" onClick={(e) => void scan(e)}>
              Add
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-ink/10 bg-white">
            <table className="w-full">
              <thead className="bg-gold-50">
                <tr>
                  <th className="th">Item</th>
                  <th className="th">Purity</th>
                  <th className="th text-right">Fine wt</th>
                  <th className="th text-right">Metal</th>
                  <th className="th text-right">Making</th>
                  <th className="th text-right">Tax</th>
                  <th className="th text-right">Total</th>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {newLines.length === 0 ? (
                  <tr>
                    <td className="td text-center text-ink-soft" colSpan={8}>
                      Scan the piece the customer is buying.
                    </td>
                  </tr>
                ) : (
                  newLines.map((l) => (
                    <tr key={l.item.id}>
                      <td className="td">
                        <span className="font-medium">{l.item.item_code}</span>
                        <span className="block text-xs text-ink-soft">{l.item.product_name}</span>
                      </td>
                      <td className="td">{l.item.purity_code}</td>
                      <td className="td num text-right">{fine(l.quote.fineWt)}</td>
                      <td className="td num text-right">{money(l.quote.metalValue)}</td>
                      <td className="td num text-right">{money(l.quote.makingAmount)}</td>
                      <td className="td num text-right">{money(l.quote.taxTotal)}</td>
                      <td className="td num text-right font-medium">{money(l.quote.lineTotal)}</td>
                      <td className="td">
                        <button
                          type="button"
                          className="text-xs text-danger underline"
                          onClick={() => setNewLines((c) => c.filter((x) => x.item.id !== l.item.id))}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="card flex flex-wrap items-end gap-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-soft">New jewellery</p>
            <p className="num text-xl font-semibold">{money(saleTotal.toFixed(2))}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-soft">Old gold credit</p>
            <p className="text-sm text-ink-soft">
              worked out on posting,
              <br />
              from the tested purity
            </p>
          </div>
          <div className="w-44">
            <Field label="Cash taken now">
              <input
                className="field num"
                inputMode="decimal"
                value={cash}
                onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="0.00"
              />
            </Field>
          </div>
          <button className="btn-primary ml-auto" disabled={!ready || create.isPending}>
            {create.isPending ? 'Posting…' : 'Post exchange'}
          </button>
        </div>
      </form>
    </>
  );
}
